import { useEffect, useRef, useState } from 'react';
import {
  CheckCircle2,
  Loader2,
  Plus,
  Send,
  Square,
  Trash2,
  Trash,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils/cn';
import { parseCurl, parseNestedStrings } from '@/lib/utils/curl';
import { useTestProxyRequest, type ProxyResponse } from '../proxy';

export interface ApiBuilderRow {
  key: string;
  value: string;
}

export type EditorMode = 'key-value' | 'raw';

export interface ApiBuilderState {
  url: string;
  method: string;
  headers: ApiBuilderRow[];
  body: ApiBuilderRow[];
  rawHeaders: string;
  rawBody: string;
  headerMode: EditorMode;
  bodyMode: EditorMode;
}

export interface ApiBuilderProps {
  initial: ApiBuilderState;
  /** The HTTP method is governed by the parent form (GET / POST). */
  method: 'GET' | 'POST';
  onMethod?: (method: 'GET' | 'POST') => void;
  /** Called on every change so the parent can mirror header/body state. */
  onStateChange?: (state: ApiBuilderState) => void;
  /**
   * Called when the user clicks Submit. Receives the URL and the
   * serialized body string (the body that will become the parent form's
   * `payload` field).
   */
  onSubmit?: (url: string, bodyString: string) => void;
}

/**
 * Postman-style API builder ported from the legacy `WebhooksSms/ApiBuilder.js`.
 * Supports cURL paste-to-parse, key-value / raw toggles for headers and body,
 * live "Test" via the proxypass endpoint, and a Submit that propagates the
 * configured URL + serialized body back to the parent form.
 */
export function ApiBuilder({
  initial,
  method,
  onMethod,
  onStateChange,
  onSubmit,
}: ApiBuilderProps) {
  const [url, setUrl] = useState(initial.url);
  const [headers, setHeaders] = useState<ApiBuilderRow[]>(
    initial.headers.length > 0 ? initial.headers : [{ key: '', value: '' }],
  );
  const [body, setBody] = useState<ApiBuilderRow[]>(
    initial.body.length > 0 ? initial.body : [{ key: '', value: '' }],
  );
  const [rawHeaders, setRawHeaders] = useState(initial.rawHeaders);
  const [rawBody, setRawBody] = useState(initial.rawBody);
  const [headerMode, setHeaderMode] = useState<EditorMode>(initial.headerMode);
  const [bodyMode, setBodyMode] = useState<EditorMode>(initial.bodyMode);

  const [response, setResponse] = useState<ProxyResponse | null>(null);
  const testReq = useTestProxyRequest();
  const abortRef = useRef<AbortController | null>(null);

  // Push state up on every change.
  useEffect(() => {
    onStateChange?.({
      url,
      method,
      headers,
      body,
      rawHeaders,
      rawBody,
      headerMode,
      bodyMode,
    });
  }, [
    url,
    method,
    headers,
    body,
    rawHeaders,
    rawBody,
    headerMode,
    bodyMode,
    onStateChange,
  ]);

  /* ----- Header / Body editing ----- */

  const addHeader = () => {
    const last = headers[headers.length - 1];
    if (last && !last.key && !last.value) return;
    setHeaders([...headers, { key: '', value: '' }]);
  };
  const updateHeader = (i: number, patch: Partial<ApiBuilderRow>) => {
    setHeaders((rows) =>
      rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    );
  };
  const removeHeader = (i: number) => {
    setHeaders((rows) => {
      const next = rows.filter((_, idx) => idx !== i);
      return next.length ? next : [{ key: '', value: '' }];
    });
  };

  const addBodyRow = () => {
    const last = body[body.length - 1];
    if (last && !last.key && !last.value) return;
    setBody([...body, { key: '', value: '' }]);
  };
  const updateBody = (i: number, patch: Partial<ApiBuilderRow>) => {
    setBody((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const removeBodyRow = (i: number) => {
    setBody((rows) => {
      const next = rows.filter((_, idx) => idx !== i);
      return next.length ? next : [{ key: '', value: '' }];
    });
  };

  /* ----- Mode toggles (preserve data on switch) ----- */

  const switchHeaderMode = (next: EditorMode) => {
    if (next === 'raw') {
      const obj = headers.reduce<Record<string, string>>((acc, { key, value }) => {
        if (key) acc[key] = value;
        return acc;
      }, {});
      setRawHeaders(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : '');
      setHeaderMode('raw');
    } else {
      try {
        const parsed = JSON.parse(rawHeaders || '{}');
        const arr = Object.entries(parsed).map(([key, val]) => ({
          key,
          value: typeof val === 'string' ? val : JSON.stringify(val),
        }));
        setHeaders(arr.length ? arr : [{ key: '', value: '' }]);
        setHeaderMode('key-value');
      } catch {
        toast.error('Invalid JSON in raw headers');
      }
    }
  };

  const switchBodyMode = (next: EditorMode) => {
    if (next === 'raw') {
      const obj = body.reduce<Record<string, unknown>>((acc, { key, value }) => {
        if (!key) return acc;
        acc[key] = parseNestedStrings(value);
        return acc;
      }, {});
      setRawBody(Object.keys(obj).length ? JSON.stringify(obj, null, 2) : '');
      setBodyMode('raw');
    } else {
      try {
        const parsed = JSON.parse(rawBody || '{}');
        const arr = Object.entries(parsed).map(([key, val]) => ({
          key,
          value: typeof val === 'string' ? val : JSON.stringify(val),
        }));
        setBody(arr.length ? arr : [{ key: '', value: '' }]);
        setBodyMode('key-value');
      } catch {
        toast.error('Invalid JSON in raw body');
      }
    }
  };

  /* ----- URL change with cURL paste-to-parse ----- */

  const handleUrlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setUrl(value);

    if (value.trim().toLowerCase().startsWith('curl')) {
      const parsed = parseCurl(value);
      setUrl(parsed.url ?? '');
      if (parsed.method && onMethod) {
        onMethod(parsed.method as 'GET' | 'POST');
      }
      const headerArray = Object.entries(parsed.headers).map(([key, v]) => ({
        key,
        value: v,
      }));
      setHeaders(headerArray.length ? headerArray : [{ key: '', value: '' }]);

      if (parsed.body) {
        try {
          const bodyObj = JSON.parse(parsed.body) as Record<string, unknown>;
          const bodyArray = Object.entries(bodyObj).map(([key, v]) => ({
            key,
            value:
              v && typeof v === 'object' && !Array.isArray(v)
                ? JSON.stringify(v)
                : String(v),
          }));
          setBody(bodyArray.length ? bodyArray : [{ key: '', value: '' }]);
          setBodyMode('key-value');
        } catch {
          const params = new URLSearchParams(parsed.body);
          const bodyArray: ApiBuilderRow[] = [];
          params.forEach((v, key) => bodyArray.push({ key, value: v }));
          setBody(bodyArray.length ? bodyArray : [{ key: '', value: '' }]);
          setBodyMode('key-value');
        }
      } else {
        setBody([{ key: '', value: '' }]);
      }
    }
  };

  /* ----- Test (proxy) ----- */

  const buildHeaderObject = (): Record<string, string> | null => {
    if (headerMode === 'raw') {
      try {
        return JSON.parse(rawHeaders || '{}');
      } catch {
        toast.error('Raw headers must be valid JSON');
        return null;
      }
    }
    return headers.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key) acc[key] = value;
      return acc;
    }, {});
  };

  const buildRequestBody = (
    headerObj: Record<string, string>,
  ): string | null => {
    if (method !== 'POST') return null;
    const contentTypeKey = Object.keys(headerObj).find(
      (k) => k.toLowerCase() === 'content-type',
    );
    const isJson = contentTypeKey
      ? (headerObj[contentTypeKey] ?? '').includes('application/json')
      : false;

    if (bodyMode === 'raw') return rawBody;

    if (isJson) {
      const bodyObj = body.reduce<Record<string, unknown>>((acc, { key, value }) => {
        if (!key) return acc;
        acc[key] = parseNestedStrings(value);
        return acc;
      }, {});
      return JSON.stringify(bodyObj);
    }

    const formData = new URLSearchParams();
    body.forEach(({ key, value }) => {
      if (key) formData.append(key, value);
    });
    if (!contentTypeKey) {
      headerObj['Content-Type'] = 'application/x-www-form-urlencoded';
    }
    return formData.toString();
  };

  const sendRequest = () => {
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    const headerObj = buildHeaderObject();
    if (!headerObj) return;
    const requestBody = buildRequestBody(headerObj);

    const controller = new AbortController();
    abortRef.current = controller;

    testReq.mutate(
      {
        payload: {
          url,
          method,
          headers: JSON.stringify(headerObj),
          body: requestBody,
        },
        signal: controller.signal,
      },
      {
        onSuccess: (res) => setResponse(res),
        onError: (err: unknown) => {
          const e = err as { name?: string; message?: string };
          if (e?.name === 'AbortError') toast.info('Request aborted');
          else toast.error(e?.message ?? 'Test request failed');
        },
        onSettled: () => {
          abortRef.current = null;
        },
      },
    );
  };

  const stopRequest = () => {
    abortRef.current?.abort();
    abortRef.current = null;
  };

  /* ----- Clear & Submit ----- */

  const handleClear = () => {
    setUrl('');
    setHeaders([{ key: '', value: '' }]);
    setBody([{ key: '', value: '' }]);
    setRawHeaders('');
    setRawBody('');
    setHeaderMode('key-value');
    setBodyMode('key-value');
    setResponse(null);
  };

  const handleSubmit = () => {
    if (!url) return;
    const bodyString =
      bodyMode === 'raw'
        ? rawBody
        : JSON.stringify(
            body.reduce<Record<string, unknown>>((acc, { key, value }) => {
              if (!key) return acc;
              acc[key] = parseNestedStrings(value);
              return acc;
            }, {}),
            null,
            2,
          );
    onSubmit?.(url, bodyString);
  };

  /* ----- Render ----- */

  const ModeToggle = ({
    value,
    onChange,
  }: {
    value: EditorMode;
    onChange: (m: EditorMode) => void;
  }) => (
    <div className="inline-flex rounded-md border border-border bg-muted p-1">
      {(['key-value', 'raw'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={cn(
            'rounded px-3 py-1 text-xs font-medium transition-colors',
            value === m
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {m === 'key-value' ? 'Key-Value' : 'Raw'}
        </button>
      ))}
    </div>
  );

  const isTesting = testReq.isPending;

  return (
    <Card className="border-border/80">
      <CardContent className="space-y-4 p-4">
        <div className="space-y-2">
          <Label htmlFor="api-url" className="font-semibold">
            Request URL
          </Label>
          <Textarea
            id="api-url"
            value={url}
            onChange={handleUrlChange}
            rows={3}
            placeholder="Enter URL or paste full cURL here"
            className="font-mono text-sm"
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h6 className="font-semibold text-foreground">Headers</h6>
            <ModeToggle value={headerMode} onChange={switchHeaderMode} />
          </div>

          {headerMode === 'key-value' ? (
            <div className="space-y-2">
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Key</th>
                      <th className="px-3 py-2 text-left font-medium">Value</th>
                      <th className="w-14 px-3 py-2 text-right font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {headers.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-1.5">
                          <Input
                            value={row.key}
                            onChange={(e) => updateHeader(i, { key: e.target.value })}
                            placeholder="Authorization"
                            className="h-8 font-mono text-xs"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <Input
                            value={row.value}
                            onChange={(e) => updateHeader(i, { value: e.target.value })}
                            placeholder="Bearer …"
                            className="h-8 font-mono text-xs"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => removeHeader(i)}
                            aria-label="Remove header"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addHeader}>
                <Plus className="h-4 w-4" />
                Add header
              </Button>
            </div>
          ) : (
            <Textarea
              value={rawHeaders}
              onChange={(e) => setRawHeaders(e.target.value)}
              rows={8}
              placeholder={'{\n  "Authorization": "Bearer …"\n}'}
              className="font-mono text-xs"
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h6 className="font-semibold text-foreground">Body</h6>
            <ModeToggle value={bodyMode} onChange={switchBodyMode} />
          </div>

          {bodyMode === 'key-value' ? (
            <div className="space-y-2">
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Key</th>
                      <th className="px-3 py-2 text-left font-medium">Value</th>
                      <th className="w-14 px-3 py-2 text-right font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {body.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-3 py-1.5">
                          <Input
                            value={row.key}
                            onChange={(e) => updateBody(i, { key: e.target.value })}
                            placeholder="to"
                            className="h-8 font-mono text-xs"
                          />
                        </td>
                        <td className="px-3 py-1.5">
                          <Input
                            value={row.value}
                            onChange={(e) => updateBody(i, { value: e.target.value })}
                            placeholder="{PHONE}"
                            className="h-8 font-mono text-xs"
                          />
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => removeBodyRow(i)}
                            aria-label="Remove body row"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addBodyRow}>
                <Plus className="h-4 w-4" />
                Add body parameter
              </Button>
            </div>
          ) : (
            <Textarea
              value={rawBody}
              onChange={(e) => setRawBody(e.target.value)}
              rows={8}
              placeholder={'{\n  "to": "{PHONE}",\n  "text": "…"\n}'}
              className="font-mono text-xs"
            />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isTesting ? (
            <Button type="button" variant="destructive" onClick={stopRequest}>
              <Square className="h-4 w-4" />
              Stop
            </Button>
          ) : (
            <Button type="button" onClick={sendRequest}>
              <Send className="h-4 w-4" />
              Test
            </Button>
          )}
          {url && !isTesting && (
            <Button type="button" variant="default" onClick={handleSubmit}>
              <CheckCircle2 className="h-4 w-4" />
              Submit
            </Button>
          )}
          <Button type="button" variant="outline" onClick={handleClear}>
            <Trash className="h-4 w-4" />
            Clear
          </Button>
          {isTesting && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Sending…
            </span>
          )}
        </div>

        {response && <ResponsePanel response={response} />}
      </CardContent>
    </Card>
  );
}

function ResponsePanel({ response }: { response: ProxyResponse }) {
  const [tab, setTab] = useState<'data' | 'headers'>('data');

  const dataText = (() => {
    try {
      const parsed =
        typeof response.data === 'object'
          ? response.data
          : JSON.parse(String(response.data));
      return JSON.stringify(parsed, null, 2);
    } catch {
      return String(response.data);
    }
  })();

  const isOk = response.status >= 200 && response.status < 300;

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex items-center justify-between">
        <h6 className="font-semibold text-foreground">Response</h6>
        <Badge
          variant="outline"
          className={cn(
            'font-mono text-xs',
            isOk
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-900',
          )}
        >
          {isOk ? (
            <CheckCircle2 className="h-3 w-3" />
          ) : (
            <XCircle className="h-3 w-3" />
          )}
          {response.status} {response.statusText}
        </Badge>
      </div>

      <div className="inline-flex rounded-md border border-border bg-muted p-1">
        {(['data', 'headers'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              tab === t
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t === 'data' ? 'Data' : 'Headers'}
          </button>
        ))}
      </div>

      {tab === 'data' ? (
        <pre className="max-h-[260px] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
          {dataText}
        </pre>
      ) : (
        <div className="max-h-[260px] overflow-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <tbody>
              {Object.entries(response.headers ?? {}).map(([k, v]) => (
                <tr key={k} className="border-t border-border first:border-t-0">
                  <td className="border-r border-border px-2 py-1.5 font-mono text-muted-foreground">
                    {k}
                  </td>
                  <td className="break-all px-2 py-1.5 font-mono">{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
